/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  LexicalCommand,
  LexicalEditor,
  NodeKey,
  RangeSelection,
} from 'lexical';
import React, { useContext, type JSX } from 'react';

import {
  $createMarkNode,
  $isMarkNode,
  $unwrapMarkNode,
  $wrapSelectionInMarkNode,
  MarkNode,
} from '@lexical/mark';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {createDOMRange} from '@lexical/selection';
import {mergeRegister, registerNestedElementResolver} from '@lexical/utils';
import {
  $addUpdateTag,
  $createRangeSelection,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COLLABORATION_TAG,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  REDO_COMMAND,
  UNDO_COMMAND,
  getDOMSelection,
  HISTORY_MERGE_TAG,
  SKIP_SCROLL_INTO_VIEW_TAG,
} from 'lexical';
import moment from 'moment';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

import { useLexicalEditorContext } from '@/components/editor/LexicalEditorContext';
import { useMarkNodesContext } from '@/components/editor/lexicalPlugins/suggestions/MarkNodesContext';
import { useCommentStoreContext, useCollabAuthorName, useCommentStore } from '@/components/lexical/commenting/CommentStoreContext';
import { $isSuggestionNode } from '@/components/editor/lexicalPlugins/suggestedEdits/ProtonNode';
import { SuggestionTypesThatCanBeEmpty } from '@/components/editor/lexicalPlugins/suggestedEdits/Types';
import { createThread, createComment, Thread, Comments, Comment } from '../../commenting';
import useModal from '../../hooks/useModal';
import Button from '../../ui/Button';
import { InlineCommentsPanelContext } from '@/components/common/sharedContexts';
import LWClickAwayListener from '@/components/common/LWClickAwayListener';
import { PlainTextEditor, useOnChange, CommentsComposer, SuggestionStatusOrActions, RESOLVE_THREAD_COMMAND, getThreadMarkId, acceptSuggestionThread, rejectSuggestionThread } from './CommentPluginComponents';
import ForumIcon from '@/components/common/ForumIcon';
import { formatSuggestionSummary } from '@/components/editor/lexicalPlugins/suggestedEdits/suggestionSummaryUtils';
import { SUGGESTION_SUMMARY_KIND } from '@/components/editor/lexicalPlugins/suggestedEdits/Utils';
import { useCurrentCollaboratorId } from '@/components/lexical/collaboration';
import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import classNames from 'classnames';

/**
 * Replacement for @lexical/selection's createRectsFromDOMRange.
 * The upstream version has two issues that cause alternating lines in
 * multiline selections to lose their highlight:
 * 1. It filters out rects that span the full width of the editor root.
 * 2. Its overlap filter treats tiny trailing rects (which browsers produce
 *    at line-wrap boundaries) as overlapping the next line's main rect,
 *    because their full-height extent creates a few pixels of cross-line
 *    vertical overlap.
 * This version only deduplicates rects that share the same visual line.
 */
function createRectsFromDOMRange(range: Range): DOMRect[] {
  const selectionRects = Array.from(range.getClientRects());
  let selectionRectsLength = selectionRects.length;
  // Sort rects from top-left to bottom-right.
  selectionRects.sort((a, b) => {
    const top = a.top - b.top;
    // Some rects match position closely, but not perfectly,
    // so we give a 3px tolerance.
    if (Math.abs(top) <= 3) {
      return a.left - b.left;
    }
    return top;
  });
  let prevRect: DOMRect | undefined;
  for (let i = 0; i < selectionRectsLength; i++) {
    const selectionRect = selectionRects[i];
    // Exclude rects that overlap preceding rects on the same visual line.
    // We only consider rects as overlapping if they share the same line
    // (tops within 3px), to avoid filtering out rects on adjacent lines
    // that have minor cross-line vertical overlap from text rendering.
    const isSameLine = prevRect && Math.abs(prevRect.top - selectionRect.top) <= 3;
    if (
      isSameLine &&
      prevRect!.left + prevRect!.width > selectionRect.left
    ) {
      selectionRects.splice(i--, 1);
      selectionRectsLength--;
      continue;
    }
    prevRect = selectionRect;
  }
  return selectionRects;
}


const styles = defineStyles('LexicalCommentPlugin', (theme: ThemeType) => ({
  commentInputBox: {
    display: 'block',
    position: 'absolute',
    width: 250,
    minHeight: 80,
    backgroundColor: theme.palette.grey[0],
    boxShadow: `0 0 5px 0 ${theme.palette.greyAlpha(0.1)}`,
    borderRadius: 5,
    zIndex: 24,
    animation: '$showInputBox 0.4s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: 0,
      height: 0,
      marginLeft: '0.5em',
      right: '-1em',
      top: 0,
      left: 'calc(50% + 0.25em)',
      boxSizing: 'border-box',
      border: `0.5em solid ${theme.palette.grey[1000]}`,
      borderColor: `transparent transparent ${theme.palette.grey[0]} ${theme.palette.grey[0]}`,
      transformOrigin: '0 0',
      transform: 'rotate(135deg)',
      boxShadow: `-3px 3px 3px 0 ${theme.palette.greyAlpha(0.05)}`,
    },
  },
  '@keyframes showInputBox': {
    '0%': {
      opacity: 0,
      transform: 'translateY(50px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  commentInputBoxButtons: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 10px 10px 10px',
    gap: 10,
  },
  commentInputBoxButton: {
    flex: 1,
  },
  commentInputBoxButtonPrimary: {
    backgroundColor: theme.palette.lexicalEditor.commentInputBoxButtonBackground,
    fontWeight: 'bold',
    color: theme.palette.grey[0],
    '&:hover': {
      backgroundColor: theme.palette.lexicalEditor.commentInputBoxButtonHoverBackground,
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[200],
      opacity: 0.5,
      cursor: 'not-allowed',
      fontWeight: 'normal',
      color: theme.palette.grey[700],
      '&:hover': {
        opacity: 0.5,
        backgroundColor: theme.palette.grey[200],
      },
    },
  },
  commentInputBoxEditor: {
    position: 'relative',
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[0],
    borderRadius: 5,
    fontSize: 15,
    caretColor: theme.palette.grey[900],
    display: 'block',
    padding: '9px 10px 10px 9px',
    minHeight: 80,
    '&:focus': {
      outline: `1px solid ${theme.palette.lexicalEditor.commentInputBoxButtonBackground}`,
    },
  },
  showCommentsButton: {
    position: 'fixed',
    top: 70,
    right: 6,
    backgroundColor: theme.palette.grey[300],
    borderRadius: 10,
    '@media (max-width: 600px)': {
      display: 'none',
    },
    '&:hover $commentsIcon': {
      opacity: 1,
    },
  },
  showCommentsButtonActive: {
    backgroundColor: theme.palette.grey[400],
  },
  commentsIcon: {
    display: 'inline-block',
    height: 20,
    width: 20,
    verticalAlign: '-10px',
    opacity: 0.5,
    transition: 'opacity 0.2s linear',
  },
  commentsPanel: {
    position: 'fixed',
    right: 86,
    width: 'min(480px, calc(100vw - 112px))',
    [theme.breakpoints.down('md')]: {
      right: 66,
      width: 'min(420px, calc(100vw - 92px))',
    },
    [theme.breakpoints.down('sm')]: {
      right: 6,
      width: 'calc(100vw - 12px)',
    },
    top: 'var(--editor-right-rail-top)',
    height: 'var(--editor-right-rail-height)',
    backgroundColor: theme.palette.panelBackground.default,
    border: theme.palette.greyBorder('1px', 0.14),
    borderRadius: 12,
    zIndex: 25,
    overflow: 'hidden',
    transition: 'top 0.2s ease-in-out, height 0.2s ease-in-out',
    ...theme.typography.commentStyle,
  },
  commentsPanelHeading: {
    padding: '12px 14px 10px',
    margin: 0,
    height: 'auto',
    fontSize: 12,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    color: theme.palette.greyAlpha(0.58),
    overflow: 'hidden',
    borderBottom: theme.palette.greyBorder('1px', 0.08),
  },
  commentsPanelCloseButton: {
    height: 20,
    width: 20,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  commentsPanelCloseButtonIcon: {
    height: 16,
    width: 16,
  },
  commentsPanelEmpty: {
    color: theme.palette.greyAlpha(0.62),
    fontSize: 15,
    textAlign: 'center',
    position: 'absolute',
    top: 'calc(50% - 15px)',
    margin: 0,
    padding: 0,
    width: '100%',
  },
  commentsPanelList: {
    padding: 0,
    listStyleType: 'none',
    margin: 0,
    width: '100%',
    position: 'absolute',
    top: 42,
    overflowY: 'auto',
    height: 'calc(100% - 42px)',
  },
  // Docked variant: fills a host container (e.g. the research workspace's
  // right-pane Comments tab) instead of floating over the page; the host
  // supplies its own heading and close affordance. The list override must
  // come after `commentsPanelList` — equal specificity, so source order
  // decides whether `top: 0` beats the floating variant's heading offset.
  commentsPanelDocked: {
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
    ...theme.typography.commentStyle,
  },
  commentsPanelListDocked: {
    top: 0,
    height: '100%',
    // The host's tab bar sits directly above the list, so the first
    // thread's top border is just clutter.
    '& > li:first-child': {
      borderTop: 'none',
    },
  },
  listComment: {
    padding: '12px 16px',
    margin: 0,
    fontSize: 15,
    position: 'relative',
    transition: 'all 0.2s linear',
    borderTop: theme.palette.greyBorder('1px', 0.08),
    '&:first-child': {
      borderTop: 'none',
    },
    '& p': {
      margin: 0,
      marginTop: 4,
      color: theme.palette.grey[900],
      lineHeight: 1.5,
      whiteSpace: 'pre-wrap',
    },
  },
  listDetails: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 2,
    fontSize: 13,
    color: theme.palette.grey[700],
  },
  commentAuthor: {
    fontWeight: 600,
    color: theme.palette.grey[900],
  },
  commentTime: {
    color: theme.palette.grey[600],
    fontSize: 12,
  },
  listThread: {
    padding: 0,
    margin: 0,
    borderBottom: theme.palette.greyBorder('1px', 0.08),
    position: 'relative',
    transition: 'all 0.2s linear',
    '&:first-child': {
      borderTop: theme.palette.greyBorder('1px', 0.08),
    },
  },
  listThreadInteractive: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  listThreadActive: {
    backgroundColor: theme.palette.grey[50],
    borderLeft: `4px solid ${theme.palette.primary.main}`, // More visible active state
    cursor: 'inherit',
    '& $listComment:hover': {
      backgroundColor: 'inherit',
    },
  },
  threadQuoteBox: {
    paddingTop: 12,
    paddingBottom: 4,
    color: theme.palette.grey[600],
    display: 'block',
    '&:hover $deleteButton': {
      opacity: 0.5,
    },
  },
  threadQuote: {
    margin: '0 16px',
    paddingLeft: 8,
    borderLeft: `3px solid ${theme.palette.grey[300]}`,
    whiteSpace: 'pre-wrap',
    '& span': {
      color: theme.palette.grey[700],
      backgroundColor: 'transparent',
      padding: 0,
      lineHeight: 1.4,
      display: 'inline',
      fontStyle: 'italic',
    },
  },
  suggestionSummary: {
    fontSize: 15,
    lineHeight: 1.5,
    marginTop: 0,
    marginLeft: 16,
    marginRight: 16,
    color: theme.palette.grey[900],
  },
  suggestionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
    marginLeft: 16,
    marginRight: 16,
    fontSize: 13,
  },
  suggestionHeaderLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  threadComments: {
    paddingLeft: 10,
    listStyleType: 'none',
    '& $listComment:first-child': {
      border: 'none',
      marginLeft: 0,
      paddingLeft: 5,
    },
    '& $listComment:first-child:last-child': {
      paddingBottom: 5,
    },
    '& $listComment': {
      paddingLeft: 10,
      borderLeft: `5px solid ${theme.palette.grey[200]}`,
      marginLeft: 5,
    },
  },
  threadEditor: {
    position: 'relative',
    paddingTop: 1,
  },
  deleteButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 30,
    height: 30,
    backgroundColor: 'transparent',
    opacity: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 1,
      filter: 'invert(45%) sepia(98%) saturate(2299%) hue-rotate(201deg) brightness(100%) contrast(92%)',
    },
  },
  deleteIcon: {
    position: 'absolute',
    left: 5,
    top: 5,
    height: 15,
    width: 15,
    verticalAlign: '-10px',
    transition: 'opacity 0.2s linear',
  },
  deletedComment: {
    opacity: 0.5,
  },
  listCommentHover: {
    '&:hover $deleteButton': {
      opacity: 0.5,
    },
  },
}));

const isSuggestionThread = (thread: Thread): boolean => thread.threadType === 'suggestion';

const getSuggestionSummaryComment = (thread: Thread): Comment | undefined => thread.comments.find((comment) => comment.commentKind === SUGGESTION_SUMMARY_KIND);

/**
 * Count the open threads whose marks are no longer in the document (eg
 * because the commented text was deleted), plus any standalone comments.
 * These can only be seen in the comments panel.
 */
function countUnanchoredComments(comments: Comments, markNodeMap: Map<string, Set<NodeKey>>): number {
  let count = 0;
  for (const commentOrThread of comments) {
    if (commentOrThread.type === 'thread') {
      const isOpen = (commentOrThread.status ?? 'open') === 'open';
      if (isOpen && !markNodeMap.get(getThreadMarkId(commentOrThread))?.size) {
        count++;
      }
    } else if (!commentOrThread.deleted) {
      count++;
    }
  }
  return count;
}

export const INSERT_INLINE_COMMAND: LexicalCommand<void> = createCommand(
  'INSERT_INLINE_COMMAND',
);

export type InsertInlineCommentAtPayload = {
  rect: DOMRect;
};

export const INSERT_INLINE_COMMENT_AT_COMMAND: LexicalCommand<InsertInlineCommentAtPayload> = createCommand(
  'INSERT_INLINE_COMMENT_AT_COMMAND',
);

type SelectionSnapshot = {
  anchorKey: NodeKey;
  anchorOffset: number;
  focusKey: NodeKey;
  focusOffset: number;
};

export type InsertInlineThreadPayload = {
  threadId: string;
  initialContent: string;
  quote?: string;
  selectionSnapshot?: SelectionSnapshot;
};

export const INSERT_INLINE_THREAD_COMMAND: LexicalCommand<InsertInlineThreadPayload> = createCommand(
  'INSERT_INLINE_THREAD_COMMAND',
);

export type UpdateInlineThreadPayload = {
  threadId: string;
  quote?: string;
  firstCommentContent?: string;
};

function createRangeSelectionFromSnapshot(
  snapshot: SelectionSnapshot,
): RangeSelection | null {
  const anchorNode = $getNodeByKey(snapshot.anchorKey);
  const focusNode = $getNodeByKey(snapshot.focusKey);
  if (!anchorNode || !focusNode || !$isTextNode(anchorNode) || !$isTextNode(focusNode)) return null;
  const anchorOffset = Math.min(snapshot.anchorOffset, anchorNode.getTextContentSize());
  const focusOffset = Math.min(snapshot.focusOffset, focusNode.getTextContentSize());
  const range = $createRangeSelection();
  range.setTextNodeRange(anchorNode, anchorOffset, focusNode, focusOffset);
  return range;
}

export const UPDATE_INLINE_THREAD_COMMAND: LexicalCommand<UpdateInlineThreadPayload> = createCommand(
  'UPDATE_INLINE_THREAD_COMMAND',
);

export type HideThreadPayload = { threadId: string };

export const HIDE_THREAD_COMMAND: LexicalCommand<HideThreadPayload> = createCommand(
  'HIDE_THREAD_COMMAND',
);


function CommentInputBox({
  editor,
  cancelAddComment,
  submitAddComment,
  anchorRect,
}: {
  cancelAddComment: () => void;
  editor: LexicalEditor;
  submitAddComment: (
    commentOrThread: Comment | Thread,
    isInlineComment: boolean,
    thread?: Thread,
    selection?: RangeSelection | null,
  ) => void;
  anchorRect: DOMRect | null;
}) {
  const classes = useStyles(styles);
  const [content, setContent] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const selectionState = useMemo(
    () => ({
      container: document.createElement('div'),
      elements: [],
    }),
    [],
  );
  const selectionRef = useRef<RangeSelection | null>(null);
  const author = useCollabAuthorName();
  const authorId = useCurrentCollaboratorId();

  const updateLocation = useCallback(() => {
    if (anchorRect) {
      const boxElem = boxRef.current;
      if (boxElem !== null) {
        const {left, width, bottom} = anchorRect;
        let correctedLeft = left + (width / 2) - 125;
        if (correctedLeft < 10) {
          correctedLeft = 10;
        }
        boxElem.style.left = `${correctedLeft}px`;
        boxElem.style.top = `${
          bottom +
          20 +
          (window.pageYOffset || document.documentElement.scrollTop)
        }px`;
      }
      const {container} = selectionState;
      const elements: Array<HTMLSpanElement> = selectionState.elements;
      for (let i = elements.length - 1; i >= 0; i--) {
        const elem = elements[i];
        container.removeChild(elem);
        elements.pop();
      }
      return;
    }

    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        selectionRef.current = selection.clone();
        const anchor = selection.anchor;
        const focus = selection.focus;
        const range = createDOMRange(
          editor,
          anchor.getNode(),
          anchor.offset,
          focus.getNode(),
          focus.offset,
        );
        const boxElem = boxRef.current;
        if (range !== null && boxElem !== null) {
          const {left, bottom, width} = range.getBoundingClientRect();
          const selectionRects = createRectsFromDOMRange(range);
          let correctedLeft =
            selectionRects.length === 1 ? left + (width / 2) - 125 : left - 125;
          if (correctedLeft < 10) {
            correctedLeft = 10;
          }
          boxElem.style.left = `${correctedLeft}px`;
          boxElem.style.top = `${
            bottom +
            20 +
            (window.pageYOffset || document.documentElement.scrollTop)
          }px`;
          const selectionRectsLength = selectionRects.length;
          const {container} = selectionState;
          const elements: Array<HTMLSpanElement> = selectionState.elements;
          const elementsLength = elements.length;

          for (let i = 0; i < selectionRectsLength; i++) {
            const selectionRect = selectionRects[i];
            let elem: HTMLSpanElement = elements[i];
            if (elem === undefined) {
              elem = document.createElement('span');
              elements[i] = elem;
              container.appendChild(elem);
            }
            const color = '255, 212, 0';
            const style = `position:absolute;top:${
              selectionRect.top +
              (window.pageYOffset || document.documentElement.scrollTop)
            }px;left:${selectionRect.left}px;height:${
              selectionRect.height
            }px;width:${
              selectionRect.width
            }px;background-color:rgba(${color}, 0.3);pointer-events:none;z-index:5;`;
            elem.style.cssText = style;
          }
          for (let i = elementsLength - 1; i >= selectionRectsLength; i--) {
            const elem = elements[i];
            container.removeChild(elem);
            elements.pop();
          }
        }
      }
    });
  }, [anchorRect, editor, selectionState]);

  useLayoutEffect(() => {
    updateLocation();
    const container = selectionState.container;
    const body = document.body;
    if (body !== null) {
      body.appendChild(container);
      return () => {
        body.removeChild(container);
      };
    }
  }, [selectionState.container, updateLocation]);

  useEffect(() => {
    window.addEventListener('resize', updateLocation);

    return () => {
      window.removeEventListener('resize', updateLocation);
    };
  }, [updateLocation]);

  const onEscape = (event: KeyboardEvent): boolean => {
    event.preventDefault();
    cancelAddComment();
    return true;
  };

  const submitComment = () => {
    if (canSubmit) {
      let quote = editor.getEditorState().read(() => {
        const selection = selectionRef.current;
        return selection ? selection.getTextContent() : '';
      });
      if (quote.length > 100) {
        quote = quote.slice(0, 99) + '…';
      }
      submitAddComment(
        createThread(quote, [createComment(content, author, authorId)]),
        true,
        undefined,
        selectionRef.current,
      );
      selectionRef.current = null;
    }
  };

  const onChange = useOnChange(setContent, setCanSubmit);

  return (
    <div className={classes.commentInputBox} ref={boxRef}>
      <PlainTextEditor
        className={classes.commentInputBoxEditor}
        onEscape={onEscape}
        onChange={onChange}
        onEnterSubmit={submitComment}
      />
      <div className={classes.commentInputBoxButtons}>
        <Button
          onClick={cancelAddComment}
          className={classes.commentInputBoxButton}>
          Cancel
        </Button>
        <Button
          onClick={submitComment}
          disabled={!canSubmit}
          className={classNames(classes.commentInputBoxButton, classes.commentInputBoxButtonPrimary)}>
          Comment
        </Button>
      </div>
    </div>
  );
}



const deleteCommentOrThreadDialogStyles = defineStyles('DeleteCommentOrThreadDialog', (theme: ThemeType) => ({
  message: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.grey[900],
    marginBottom: 24,
  },
  buttons: {
  }
}));

function ShowDeleteCommentOrThreadDialog({
  commentOrThread,
  deleteCommentOrThread,
  onClose,
  thread = undefined,
}: {
  commentOrThread: Comment | Thread;
  deleteCommentOrThread: (comment: Comment | Thread, thread?: Thread) => void;
  onClose: () => void;
  thread?: Thread;
}): JSX.Element {
  const classes = useStyles(deleteCommentOrThreadDialogStyles);
  return (
    <>
      <div className={classes.message}>
        Are you sure you want to delete this {commentOrThread.type}?
      </div>
      <div className={classes.buttons}>
        <Button
          onClick={() => {
            deleteCommentOrThread(commentOrThread, thread);
            onClose();
          }}>
          Delete
        </Button>{' '}
        <Button
          onClick={() => {
            onClose();
          }}>
          Cancel
        </Button>
      </div>
    </>
  );
}

function CommentsPanelListComment({
  comment,
  deleteComment,
  thread,
  actionButton,
}: {
  comment: Comment;
  deleteComment: (
    commentOrThread: Comment | Thread,
    // eslint-disable-next-line no-shadow
    thread?: Thread,
  ) => void;
  thread?: Thread;
  actionButton?: React.ReactNode;
}): JSX.Element {
  const classes = useStyles(styles);
  const [modal, showModal] = useModal();

  return (
    <li className={classNames(classes.listComment, classes.listCommentHover)}>
      {actionButton}
      <div className={classes.listDetails}>
        <span className={classes.commentAuthor}>
          {comment.author}
        </span>
        <span className={classes.commentTime}>
          {moment(comment.timeStamp).format('MMMM DD, YYYY, h:mm A')}
        </span>
      </div>
      <p
        className={comment.deleted ? classes.deletedComment : ''}>
        {comment.content}
      </p>
      {/* TODO: Figure out the right design for individual comment deletion.
          The previous implementation used a trash icon per comment with a
          confirmation dialog. Commenting out until we decide on the UX.
      {!comment.deleted && (
        <>
          <Button
            onClick={() => {
              showModal('Delete Comment', (onClose) => (
                <ShowDeleteCommentOrThreadDialog
                  commentOrThread={comment}
                  deleteCommentOrThread={deleteComment}
                  thread={thread}
                  onClose={onClose}
                />
              ));
            }}
            className={classes.deleteButton}>
            <Trash3Icon className={classes.deleteIcon} />
          </Button>
          {modal}
        </>
      )} */}
    </li>
  );
}

function CommentsPanelList({
  activeIDs,
  comments,
  deleteCommentOrThread,
  listRef,
  submitAddComment,
  markNodeMap,
  docked,
}: {
  activeIDs: Array<string>;
  comments: Comments;
  deleteCommentOrThread: (
    commentOrThread: Comment | Thread,
    thread?: Thread,
  ) => void;
  listRef: {current: null | HTMLUListElement};
  markNodeMap: Map<string, Set<NodeKey>>;
  submitAddComment: (
    commentOrThread: Comment | Thread,
    isInlineComment: boolean,
    thread?: Thread,
  ) => void;
  docked?: boolean;
}): JSX.Element {
  const classes = useStyles(styles);
  const [editor] = useLexicalComposerContext();
  const { commentStore } = useCommentStoreContext();
  const [counter, setCounter] = useState(0);
  const [modal, showModal] = useModal();

  useEffect(() => {
    // Used to keep the time stamp up to date
    const id = setTimeout(() => {
      setCounter(counter + 1);
    }, 10000);

    return () => {
      clearTimeout(id);
    };
  }, [counter]);

  useEffect(() => {
    if (activeIDs.length === 0 || !listRef.current) return;
    const activeThreadId = activeIDs[0];
    const activeElement = listRef.current.querySelector(`[data-thread-id="${CSS.escape(activeThreadId)}"]`);
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeIDs, listRef]);

  return (
    <ul className={classNames(classes.commentsPanelList, docked && classes.commentsPanelListDocked)} ref={listRef}>
      {comments.map((commentOrThread) => {
        const id = commentOrThread.id;
        if (commentOrThread.type === 'thread') {
          const isSuggestion = isSuggestionThread(commentOrThread);
          const suggestionSummaryComment = isSuggestion
            ? getSuggestionSummaryComment(commentOrThread)
            : undefined;

          const suggestionSummaryText = suggestionSummaryComment
            ? formatSuggestionSummary(suggestionSummaryComment.content)
            : null;

          const suggestionStatus = commentOrThread.status ?? 'open';
          if (suggestionStatus !== 'open') {
            return null;
          }
          const threadMarkId = getThreadMarkId(commentOrThread);
          const threadComments = isSuggestion
            ? commentOrThread.comments.filter(
                (comment) => comment.commentKind !== SUGGESTION_SUMMARY_KIND,
              )
            : commentOrThread.comments;

          const handleClickThread = () => {
            const markNodeKeys = markNodeMap.get(threadMarkId);
            if (
              markNodeKeys !== undefined &&
              (activeIDs === null || activeIDs.indexOf(threadMarkId) === -1)
            ) {
              const activeElement = document.activeElement;
              // Move selection to the start of the mark, so that we
              // update the UI with the selected thread.
              editor.update(
                () => {
                  const markNodeKey = Array.from(markNodeKeys)[0];
                  const markNode = $getNodeByKey(markNodeKey);
                  if ($isMarkNode(markNode)) {
                    markNode.selectStart();
                  } else if ($isSuggestionNode(markNode)) {
                    // For empty suggestion nodes (split, join, etc.) that use
                    // the paddingRight overflow trick, placing the caret inside
                    // them causes the parent's overflow:hidden to scroll content
                    // off-screen. Select the parent's start instead.
                    const suggestionType = markNode.getSuggestionTypeOrThrow();
                    if (SuggestionTypesThatCanBeEmpty.includes(suggestionType)) {
                      const parent = markNode.getParent();
                      if (parent) {
                        parent.selectEnd();
                      }
                    } else {
                      markNode.selectStart();
                    }
                  }
                },
                {
                  // Suppress Lexical's default scroll-to-top behavior
                  tag: SKIP_SCROLL_INTO_VIEW_TAG,
                  onUpdate() {
                    // Scroll the mark node into view centered in the viewport
                    const markNodeKey = Array.from(markNodeKeys)[0];
                    const domElement = editor.getElementByKey(markNodeKey);
                    if (domElement !== null) {
                      // Suggestion nodes like split/join use a paddingRight
                      // overflow trick. Calling scrollIntoView on them causes the
                      // parent's overflow:hidden to scroll horizontally, pushing
                      // content off-screen. Use the parent as scroll target and
                      // reset scrollLeft afterwards.
                      const hasOverflowTrick = domElement.classList.contains('split') || domElement.classList.contains('join');
                      const scrollTarget = hasOverflowTrick
                        ? (domElement.parentElement ?? domElement)
                        : domElement;
                      scrollTarget.scrollIntoView({behavior: 'smooth', block: 'center'});
                      if (hasOverflowTrick && domElement.parentElement) {
                        domElement.parentElement.scrollLeft = 0;
                      }
                    }
                    // Restore selection to the previous element
                    if (activeElement !== null) {
                      (activeElement as HTMLElement).focus();
                    }
                  },
                },
              );
            }
          };

          const showEditor = (commentOrThread.status ?? 'open') === 'open';

          return (
            <li
              key={id}
              data-thread-id={threadMarkId}
              onClick={handleClickThread}
              className={classNames(
                classes.listThread,
                { [classes.listThreadInteractive]: markNodeMap.has(threadMarkId) },
                { [classes.listThreadActive]: activeIDs.indexOf(threadMarkId) !== -1 }
              )}>
              <div className={classes.threadQuoteBox}>
                {!isSuggestion && (
                  <blockquote className={classes.threadQuote}>
                    {commentOrThread.quote}
                  </blockquote>
                )}
                {isSuggestion && suggestionSummaryText && (
                  <>
                    {suggestionSummaryComment && (
                      <div className={classes.suggestionHeader}>
                        <div className={classes.suggestionHeaderLeft}>
                          <span className={classes.commentAuthor}>
                            {suggestionSummaryComment.author}
                          </span>
                          <span className={classes.commentTime}>
                            {moment(suggestionSummaryComment.timeStamp).format('MMMM DD, YYYY, h:mm A')}
                          </span>
                        </div>
                        <SuggestionStatusOrActions
                          status={suggestionStatus}
                          suggestionAuthorId={suggestionSummaryComment?.authorId}
                          onAccept={() => acceptSuggestionThread(editor, commentOrThread)}
                          onReject={() => rejectSuggestionThread(editor, commentOrThread)}
                        />
                      </div>
                    )}
                    <div className={classes.suggestionSummary}>
                      {suggestionSummaryText}
                    </div>
                  </>
                )}
              </div>
              <ul className={classes.threadComments}>
                {threadComments.map((comment, index) => (
                  <CommentsPanelListComment
                    key={comment.id}
                    comment={comment}
                    deleteComment={deleteCommentOrThread}
                    thread={commentOrThread}
                    actionButton={index === 0 && !isSuggestion ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          editor.dispatchCommand(RESOLVE_THREAD_COMMAND, {
                            threadId: commentOrThread.id,
                            markId: threadMarkId,
                          });
                        }}
                        className={classes.deleteButton}
                        title="Resolve thread">
                        <ForumIcon icon="Check" className={classes.deleteIcon} />
                      </button>
                    ) : undefined}
                  />
                ))}
              </ul>
              {showEditor && <div className={classes.threadEditor}>
                <CommentsComposer
                  submitAddComment={submitAddComment}
                  thread={commentOrThread}
                  placeholder="Reply to comment..."
                />
              </div>}
            </li>
          );
        }
        return (
          <CommentsPanelListComment
            key={id}
            comment={commentOrThread}
            deleteComment={deleteCommentOrThread}
          />
        );
      })}
    </ul>
  );
}

function CommentsPanel({
  panelRef,
  activeIDs,
  deleteCommentOrThread,
  comments,
  submitAddComment,
  markNodeMap,
  docked,
}: {
  panelRef: React.RefObject<HTMLDivElement | null>;
  activeIDs: Array<string>;
  comments: Comments;
  deleteCommentOrThread: (
    commentOrThread: Comment | Thread,
    thread?: Thread,
  ) => void;
  markNodeMap: Map<string, Set<NodeKey>>;
  submitAddComment: (
    commentOrThread: Comment | Thread,
    isInlineComment: boolean,
    thread?: Thread,
  ) => void;
  docked?: boolean;
}): JSX.Element {
  const classes = useStyles(styles);
  const listRef = useRef<HTMLUListElement>(null);
  const isEmpty = comments.length === 0;
  const { setShowComments } = useContext(InlineCommentsPanelContext);

  return (
    <div ref={panelRef} className={docked ? classes.commentsPanelDocked : classes.commentsPanel}>
      {!docked && <h2 className={classes.commentsPanelHeading}>
        Comments
        <Button onClick={() => setShowComments(false)} className={classes.commentsPanelCloseButton}>
          <ForumIcon icon="Close" className={classes.commentsPanelCloseButtonIcon} />
        </Button>
      </h2>}
      {isEmpty ? (
        <div className={classes.commentsPanelEmpty}>No Comments</div>
      ) : (
        <CommentsPanelList
          activeIDs={activeIDs}
          comments={comments}
          deleteCommentOrThread={deleteCommentOrThread}
          listRef={listRef}
          submitAddComment={submitAddComment}
          markNodeMap={markNodeMap}
          docked={docked}
        />
      )}
    </div>
  );
}

export default function CommentPlugin(): JSX.Element {
  const classes = useStyles(styles);
  const { supportsCollabComments } = useLexicalEditorContext();
  const [editor] = useLexicalComposerContext();
  const { commentStore } = useCommentStoreContext();
  const comments = useCommentStore(commentStore);
  const author = useCollabAuthorName();
  const authorId = useCurrentCollaboratorId();
  const { markNodeMap, activeIDs, activeAnchorKey } = useMarkNodesContext();
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentAnchorRect, setCommentAnchorRect] = useState<DOMRect | null>(
    null,
  );
  const { showComments, setShowComments, setCommentCount, setUnanchoredCommentCount, panelPortalEl } = useContext(InlineCommentsPanelContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const replyActivatedRef = useRef(false);
  const cancelAddComment = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      // Restore selection
      if (selection !== null) {
        selection.dirty = true;
      }
    });
    setCommentAnchorRect(null);
    setShowCommentInput(false);
  }, [editor]);

  const deleteCommentOrThread = useCallback(
    (comment: Comment | Thread, thread?: Thread) => {
      if (comment.type === 'comment') {
        const deletionInfo = commentStore.deleteCommentOrThread(
          comment,
          thread,
        );
        if (!deletionInfo) {
          return;
        }
        const {markedComment, index} = deletionInfo;
        commentStore.addComment(markedComment, thread, index);
      } else {
        commentStore.deleteCommentOrThread(comment);
        // Remove ids from associated marks
        const id = thread !== undefined ? thread.id : comment.id;
        const markNodeKeys = markNodeMap.get(id);
        if (markNodeKeys !== undefined) {
          // Do async to avoid causing a React infinite loop
          setTimeout(() => {
            editor.update(() => {
              for (const key of markNodeKeys) {
                const node: null | MarkNode = $getNodeByKey(key);
                if ($isMarkNode(node)) {
                  node.deleteID(id);
                  if (node.getIDs().length === 0) {
                    $unwrapMarkNode(node);
                  }
                }
              }
            });
          });
        }
      }
    },
    [commentStore, editor, markNodeMap],
  );

  const submitAddComment = useCallback(
    (
      commentOrThread: Comment | Thread,
      isInlineComment: boolean,
      thread?: Thread,
      selection?: RangeSelection | null,
    ) => {
      commentStore.addComment(commentOrThread, thread);
      if (isInlineComment) {
        editor.update(() => {
          if ($isRangeSelection(selection)) {
            const isBackward = selection.isBackward();
            const id = commentOrThread.id;

            // Wrap content in a MarkNode
            $wrapSelectionInMarkNode(selection, isBackward, id);
          }
        });
        setShowCommentInput(false);
        setCommentAnchorRect(null);
      }
    },
    [commentStore, editor],
  );

  useEffect(() => {
    if (!showComments) {
      replyActivatedRef.current = false;
      return;
    }
    const panelEl = panelRef.current;
    if (!panelEl) return;
    const handleFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && e.target.getAttribute('contenteditable') === 'true') {
        replyActivatedRef.current = true;
      }
    };
    panelEl.addEventListener('focusin', handleFocusIn);
    return () => panelEl.removeEventListener('focusin', handleFocusIn);
  }, [showComments]);

  const isRedoOperationRef = useRef(false);

  useEffect(() => {
    const markNodeKeysToIDs: Map<NodeKey, Array<string>> = new Map();

    return mergeRegister(
      // Track undo vs redo for thread status restoration
      editor.registerCommand(UNDO_COMMAND, () => {
        isRedoOperationRef.current = false;
        return false;
      }, COMMAND_PRIORITY_CRITICAL),
      editor.registerCommand(REDO_COMMAND, () => {
        isRedoOperationRef.current = true;
        return false;
      }, COMMAND_PRIORITY_CRITICAL),
      registerNestedElementResolver<MarkNode>(
        editor,
        MarkNode,
        (from: MarkNode) => {
          return $createMarkNode(from.getIDs());
        },
        (from: MarkNode, to: MarkNode) => {
          // Merge the IDs
          const ids = from.getIDs();
          ids.forEach((id) => {
            to.addID(id);
          });
        },
      ),
      editor.registerMutationListener(
        MarkNode,
        (mutations, { updateTags }) => {
          const isCollaborationUpdate = updateTags.has('collaboration');
          const idsAppeared: string[] = [];
          const idsFullyDestroyed: string[] = [];

          editor.getEditorState().read(() => {
            for (const [key, mutation] of mutations) {
              const node: null | MarkNode = $getNodeByKey(key);
              let ids: NodeKey[] = [];

              if (mutation === 'destroyed') {
                ids = markNodeKeysToIDs.get(key) || [];
              } else if ($isMarkNode(node)) {
                ids = node.getIDs();
              }

              for (let i = 0; i < ids.length; i++) {
                const id = ids[i];
                let markNodeKeys = markNodeMap.get(id);
                markNodeKeysToIDs.set(key, ids);

                if (mutation === 'destroyed') {
                  if (markNodeKeys !== undefined) {
                    markNodeKeys.delete(key);
                    if (markNodeKeys.size === 0) {
                      markNodeMap.delete(id);
                      if (!isCollaborationUpdate) {
                        idsFullyDestroyed.push(id);
                      }
                    }
                  }
                } else {
                  if (markNodeKeys === undefined) {
                    markNodeKeys = new Set();
                    markNodeMap.set(id, markNodeKeys);
                    if (!isCollaborationUpdate) {
                      idsAppeared.push(id);
                    }
                  }
                  if (!markNodeKeys.has(key)) {
                    markNodeKeys.add(key);
                  }
                }
              }
            }
          });

          if (isCollaborationUpdate || !commentStore.isSynced()) return;

          // Reopen archived comment threads when their marks reappear (undo).
          // For comment threads the mark ID is the thread ID; for suggestion
          // threads it's thread.markID. getThreadByMarkID only checks markID,
          // so we fall back to checking thread IDs directly.
          const findThreadByMarkOrId = (markId: string) => {
            return commentStore.getThreadByMarkID(markId)
              ?? commentStore.getComments().find(
                (c): c is Thread => c.type === 'thread' && c.id === markId
              );
          };

          for (const id of idsAppeared) {
            const thread = findThreadByMarkOrId(id);
            if (thread && thread.status === 'archived') {
              commentStore.updateThread(thread.id, {
                statusBeforeReopen: thread.status,
                status: 'open',
              });
            }
          }

          // Restore status when marks are destroyed again (redo)
          const isRedo = isRedoOperationRef.current;
          for (const id of idsFullyDestroyed) {
            const thread = findThreadByMarkOrId(id);
            if (thread && isRedo && thread.statusBeforeReopen) {
              commentStore.updateThread(thread.id, {
                status: thread.statusBeforeReopen,
                statusBeforeReopen: null,
              });
            }
          }
        },
        {skipInitialization: false},
      ),
      // Clear the redo flag after each update
      editor.registerUpdateListener(() => {
        queueMicrotask(() => {
          isRedoOperationRef.current = false;
        });
      }),
      editor.registerUpdateListener(({editorState, tags}) => {
        editorState.read(() => {
          const selection = $getSelection();
          if (!tags.has(COLLABORATION_TAG) && $isRangeSelection(selection)) {
            setShowCommentInput(false);
          }
        });
      }),
      editor.registerCommand(
        INSERT_INLINE_COMMAND,
        () => {
          const domSelection = getDOMSelection(editor._window);
          if (domSelection !== null) {
            domSelection.removeAllRanges();
          }
          setCommentAnchorRect(null);
          setShowCommentInput(true);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        INSERT_INLINE_COMMENT_AT_COMMAND,
        (payload) => {
          const domSelection = getDOMSelection(editor._window);
          if (domSelection !== null) {
            domSelection.removeAllRanges();
          }
          setCommentAnchorRect(payload.rect);
          setShowCommentInput(true);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        INSERT_INLINE_THREAD_COMMAND,
        (payload) => {
          let selection = $getSelection();
          if (payload.selectionSnapshot) {
            const snapshotSelection = createRangeSelectionFromSnapshot(
              payload.selectionSnapshot,
            );
            if (snapshotSelection) {
              $setSelection(snapshotSelection);
              selection = snapshotSelection;
            }
          }
          if (!$isRangeSelection(selection)) return false;
          if (selection.isCollapsed()) return false;

          const quote = payload.quote ?? selection.getTextContent();
          const threadId = payload.threadId;

          const existing = commentStore
            .getComments()
            .some((c) => c.type === 'thread' && c.id === threadId);

          if (!existing) {
            const thread = createThread(
              quote,
              [createComment(payload.initialContent, author, authorId)],
              threadId,
            );
            commentStore.addComment(thread);
          }

          const isBackward = selection.isBackward();
          // MarkNodes are purely presentational metadata; creating them should not
          // introduce extra undo steps.
          $addUpdateTag(HISTORY_MERGE_TAG);
          $wrapSelectionInMarkNode(selection, isBackward, threadId);

          setShowCommentInput(false);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        UPDATE_INLINE_THREAD_COMMAND,
        (payload) => {
          commentStore.updateThread(payload.threadId, {
            quote: payload.quote,
            firstCommentContent: payload.firstCommentContent,
          });
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        HIDE_THREAD_COMMAND,
        (payload) => {
          const thread = commentStore
            .getComments()
            .find((c) => c.type === 'thread' && c.id === payload.threadId) as Thread | undefined;
          if (thread) {
            commentStore.deleteCommentOrThread(thread);
          }

          const markNodeKeys = markNodeMap.get(payload.threadId);
          if (markNodeKeys !== undefined) {
            // Important: we don't want deferred mark cleanup to run *after* undo/redo has restored
            // the thread, since that can invalidate selection points. We therefore:
            // - run cleanup as a microtask (so it happens promptly)
            // - and skip cleanup if the thread has been recreated in the meantime.
            queueMicrotask(() => {
              const threadStillAbsent = commentStore
                .getComments()
                .every((c) => c.type !== 'thread' || c.id !== payload.threadId);
              if (!threadStillAbsent) return;

              editor.update(
                () => {
                  // MarkNodes are UI-only metadata; ensure this doesn't create extra Lexical undo steps.
                  $addUpdateTag(HISTORY_MERGE_TAG);
                  // This is derived UI state. It should sync to Yjs so collaborators converge, but
                  // must not be tracked by Yjs UndoManager (or it clears redo).
                  $addUpdateTag('fmDerivedUi');
                  for (const key of markNodeKeys) {
                    const node: null | MarkNode = $getNodeByKey(key);
                    if ($isMarkNode(node)) {
                      node.deleteID(payload.threadId);
                      if (node.getIDs().length === 0) {
                        $unwrapMarkNode(node);
                      }
                    }
                  }
                },
                { tag: HISTORY_MERGE_TAG },
              );
            });
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        RESOLVE_THREAD_COMMAND,
        (payload) => {
          // Unwrap the mark nodes to remove the highlight
          const markNodeKeys = markNodeMap.get(payload.markId);
          if (markNodeKeys !== undefined) {
            for (const key of markNodeKeys) {
              const node: null | MarkNode = $getNodeByKey(key);
              if ($isMarkNode(node)) {
                node.deleteID(payload.markId);
                if (node.getIDs().length === 0) {
                  $unwrapMarkNode(node);
                }
              }
            }
          }
          // Archive the thread. The MarkNode mutation listener handles
          // reopening on undo and re-archiving on redo.
          commentStore.updateThread(payload.threadId, {
            status: 'archived',
            statusBeforeReopen: null,
          });
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [author, authorId, commentStore, editor, markNodeMap]);

  const onAddComment = () => {
    editor.dispatchCommand(INSERT_INLINE_COMMAND, undefined);
  };

  useEffect(() => {
    if (!supportsCollabComments) return;
    // Count only open threads — resolved/accepted/rejected threads stay in
    // the store but aren't active discussion.
    const openCount = comments.filter(
      (commentOrThread) => commentOrThread.type !== 'thread' || (commentOrThread.status ?? 'open') === 'open',
    ).length;
    setCommentCount(openCount);
  }, [supportsCollabComments, comments, setCommentCount]);

  useEffect(() => {
    if (!supportsCollabComments) return;
    // markNodeMap is mutated in place by mark/suggestion mutation listeners,
    // which run before update listeners, so recount after every update.
    const refresh = () => {
      setUnanchoredCommentCount(countUnanchoredComments(comments, markNodeMap));
    };
    refresh();
    return editor.registerUpdateListener(refresh);
  }, [supportsCollabComments, comments, editor, markNodeMap, setUnanchoredCommentCount]);

  return (
    <>
      {showCommentInput &&
        createPortal(
          <CommentInputBox
            editor={editor}
            cancelAddComment={cancelAddComment}
            submitAddComment={submitAddComment}
            anchorRect={commentAnchorRect}
          />,
          document.body,
        )}
      {/* When the host supplies a portal target (e.g. the research
          workspace's right-pane Comments tab), the panel renders docked
          inside it — the host owns open/close, so no click-away. Otherwise
          it floats over the page (the post-editor behavior). */}
      {showComments && supportsCollabComments && (panelPortalEl
        ? createPortal(
          <CommentsPanel
            panelRef={panelRef}
            comments={[...comments].reverse()}
            submitAddComment={submitAddComment}
            deleteCommentOrThread={deleteCommentOrThread}
            activeIDs={activeIDs}
            markNodeMap={markNodeMap}
            docked
          />,
          panelPortalEl,
        )
        : createPortal(
          <LWClickAwayListener onClickAway={(e) => {
            if (replyActivatedRef.current) return;
            const target = e.target;
            if (target instanceof Element && target.closest('mark')) return;
            setShowComments(false);
          }}>
            <CommentsPanel
              panelRef={panelRef}
              comments={[...comments].reverse()}
              submitAddComment={submitAddComment}
              deleteCommentOrThread={deleteCommentOrThread}
              activeIDs={activeIDs}
              markNodeMap={markNodeMap}
            />
          </LWClickAwayListener>,
          document.body,
        ))}
    </>
  );
}
